import { DialogActions, DialogContent, Link, Unstable_Grid2 as Grid } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { LoadingButton } from '@/components/common/LoadingButton';
import { FormTextField } from '@/components/HookForm';
import { dialogClose } from '@/modules/MuiDialog';
import { toast } from 'react-toastify';
import { patchMallBusinessInfo } from '@/services/mall.service';
import { uploadImageToStorage } from '@/services/proxy.service';
import SquareThumbInput from '@/components/FileInput/SquareThumbInput';
import DialogTitleBox from '@/modules/MuiDialog/DialogTitleBox';
import { useTranslation } from 'react-i18next';

interface CertFieldDialogProps {
    mallId: string;
    /** 번호 필드 patch 키 (businessRegistrationNumber | mailOrderSalesRegistrationNumber) */
    field: string;
    label: string;
    value?: string;
    /** 증빙서류 URL patch 키 (businessRegistrationCertificateUrl | mailOrderSalesRegistrationCertificateUrl) */
    certField: string;
    certLabel: string;
    certDescription?: string;
    /** 기존에 올린 서류 URL (있으면 '기존 서류 보기' 링크 노출) */
    existingCertUrl?: string;
    /** 저장 직전 번호 가공(정규화) */
    transform?: (v: string) => string;
}

/**
 * 번호 + 증빙서류를 함께 수정하는 단일항목 다이얼로그.
 * 사업자등록번호/통신판매번호는 서류 업로드가 딸려 있어 단순 텍스트 편집으로 쪼갤 수 없어 별도 처리.
 * 서류를 새로 올리지 않으면 번호만 저장한다(기존 서류 유지).
 */
export const CertFieldDialog = (props: CertFieldDialogProps) => {
    const { t } = useTranslation();
    const [certFile, setCertFile] = useState<File | Blob | undefined>();
    const form = useForm({ defaultValues: { value: props.value || '' } });
    const {
        handleSubmit,
        formState: { isSubmitting },
    } = form;

    async function formSubmit(data: any) {
        const raw = data?.value || '';
        const next = props.transform ? props.transform(raw) : raw;
        const isSameNumber = next === (props.value || '');
        // 번호도 그대로고 새 서류도 없으면 그냥 닫기
        if (isSameNumber && !certFile) {
            dialogClose();
            return;
        }
        try {
            const payload: Record<string, string> = {};
            if (!isSameNumber) payload[props.field] = next;
            if (certFile) {
                const url = await uploadImageToStorage([certFile], 1500).then((r) => (!!r ? r.url[0] : ''));
                if (url) payload[props.certField] = url;
            }
            await patchMallBusinessInfo(props.mallId, payload as any);
            toast.success(t('common.updated'));
            dialogClose();
        } catch (error) {
            toast.error(t('mall.businessInfoUpdateFailed'));
        }
    }

    return (
        <>
            <DialogTitleBox title={`${props.label} ${t('common.edit')}`} />
            <DialogContent>
                <Grid container>
                    <Grid xs={12}>
                        <FormTextField
                            label={props.label}
                            name={'value'}
                            form={form as any}
                            formTrigger={'change'}
                            defaultValue={''}
                        />
                    </Grid>
                    <Grid xs={12}>
                        {!!props.existingCertUrl && (
                            <Link typography={'caption'} href={props.existingCertUrl} target={'_blank'}>
                                {props.certLabel}
                            </Link>
                        )}
                        <SquareThumbInput
                            label={props.certLabel}
                            description={props.certDescription}
                            onChanged={(file) => {
                                setCertFile(file);
                            }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <LoadingButton
                    isLoading={isSubmitting}
                    onClick={async () => {
                        await handleSubmit(formSubmit)();
                    }}
                >
                    {t('common.edit')}
                </LoadingButton>
                <LoadingButton
                    color={'error'}
                    onClick={() => {
                        dialogClose();
                    }}
                >
                    {t('common.cancel')}
                </LoadingButton>
            </DialogActions>
        </>
    );
};

export default CertFieldDialog;
